
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, CreditCard, Share2, Users, Sparkles, TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import CreditBlock from '@/components/molecules/CreditBlock';

const PatientDashboardV2 = () => {
  // Fetch user's current credit balance
  const { data: creditBalance = 0 } = useQuery({
    queryKey: ['user-credit-balance'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;
      
      const { data, error } = await supabase.rpc('get_user_credit_balance', {
        user_uuid: user.id
      });
      
      if (error) throw error;
      return data || 0;
    }
  });

  // Fetch user's membership status
  const { data: membershipStatus } = useQuery({
    queryKey: ['user-membership'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('membership_status, membership_tier, first_name')
        .eq('user_id', user.id)
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch recent treatment plans
  const { data: recentPlans = [] } = useQuery({
    queryKey: ['recent-treatment-plans'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('treatment_plans')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(3);
      
      if (error) throw error;
      return data || [];
    }
  });

  const isMember = membershipStatus?.membership_status === 'active';
  const firstName = membershipStatus?.first_name || 'Beautiful';

  const quickActions = [
    {
      icon: Calendar,
      label: 'Book Treatment',
      color: 'bg-juv-terracotta',
      href: '/book'
    },
    {
      icon: Share2,
      label: 'Refer Friends',
      color: 'bg-juv-deep-chestnut',
      href: '/v2/patients/refer'
    },
    {
      icon: Sparkles,
      label: 'View Plan',
      color: 'bg-juv-terracotta',
      href: '/v2/patients/planner'
    },
    {
      icon: CreditCard,
      label: 'Glow Wallet',
      color: 'bg-juv-deep-chestnut',
      href: '/v2/patients/wallet'
    }
  ];

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Welcome Section */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-juv-deep-chestnut">
          Welcome back, {firstName}! ✨
        </h1>
        <p className="text-lg text-juv-deep-chestnut/70">
          Ready to continue your glow journey?
        </p>
        {isMember && (
          <Badge className="bg-juv-terracotta text-white px-4 py-2 text-sm">
            JUV Member - Premium Access
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Credit Balance */}
        <div className="lg:col-span-1">
          <CreditBlock 
            balance={creditBalance}
            isMember={isMember}
            creditsNeeded={0}
            creditsSufficient={true}
          />
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <Card className="border-juv-rose-clay/20 bg-white">
            <CardHeader>
              <CardTitle className="text-juv-deep-chestnut">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {quickActions.map((action, index) => (
                  <Button
                    key={index}
                    asChild
                    className={`${action.color} hover:opacity-90 text-white h-20 flex flex-col space-y-2`}
                  >
                    <a href={action.href}>
                      <action.icon className="h-6 w-6" />
                      <span className="text-sm font-medium">{action.label}</span>
                    </a>
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Activity */}
      <Card className="border-juv-rose-clay/20 bg-white">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-juv-deep-chestnut">
            <TrendingUp className="h-5 w-5" />
            <span>Recent Activity</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentPlans.length > 0 ? (
            <div className="space-y-4">
              {recentPlans.map((plan) => (
                <div key={plan.id} className="flex items-center justify-between p-4 bg-juv-soft-blush rounded-lg">
                  <div>
                    <p className="font-medium text-juv-deep-chestnut">
                      Treatment Plan for {plan.patient_name}
                    </p>
                    <p className="text-sm text-juv-deep-chestnut/70">
                      Created {new Date(plan.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-juv-terracotta">${plan.total_cost.toFixed(2)}</p>
                    <Badge variant="outline" className="text-xs">
                      {plan.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Sparkles className="h-12 w-12 text-juv-terracotta mx-auto mb-4" />
              <p className="text-juv-deep-chestnut/70">No treatment plans yet</p>
              <Button
                asChild
                className="mt-4 bg-juv-terracotta hover:bg-juv-deep-chestnut text-white"
              >
                <a href="/v2/patients/planner">Create Your First Plan</a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Membership CTA */}
      {!isMember && (
        <Card className="border-juv-terracotta/30 bg-gradient-to-r from-juv-soft-blush to-white">
          <CardContent className="p-6 text-center">
            <h3 className="text-xl font-bold text-juv-deep-chestnut mb-2">
              Unlock Exclusive Member Benefits
            </h3>
            <p className="text-juv-deep-chestnut/70 mb-4">
              Save on every treatment and earn bonus credits with JUV Membership
            </p>
            <Button
              asChild
              className="bg-juv-terracotta hover:bg-juv-deep-chestnut text-white"
            >
              <a href="/v2/patients/membership">Learn More</a>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PatientDashboardV2;
